import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useOrderContext } from '../../../contexts/OrderContext';
import { usePositionContext } from '../../../contexts/PositionContext';
import { useAccount } from '../../../contexts/AccountContext';
import { useAccountType } from '../../../contexts/AccountTypeContext';

interface RSIStrategyProps {
  symbol: string;
  currentPrice?: string;
  onSignalUpdate?: (signal: 'buy' | 'sell' | null) => void;
  onConfigUpdate?: (config: any) => void;
  isRunning?: boolean;
  initialConfig?: RSIConfig; // Add prop for initial configuration
}

interface RSIConfig {
  period: number;
  overbought: number;
  oversold: number;
  isActive: boolean;
  tradeAmountPercent: number;
  quantity: number;
  profitThreshold: number;
  lossThreshold: number;
}

const RSIStrategy: React.FC<RSIStrategyProps> = ({ 
  symbol, 
  currentPrice, 
  onSignalUpdate, 
  onConfigUpdate, 
  isRunning: externalIsRunning,
  initialConfig
}) => {
  const { theme } = useTheme();
  const { setOrderDetails, setShowOrderConfirmation, showConfirmationPopup } = useOrderContext();
  const { addOpenPosition, openPositions } = usePositionContext();
  const { accountBalance, updateBalance } = useAccount();
  const { accountType } = useAccountType();

  const [config, setConfig] = useState<RSIConfig>(() => {
    // Use initialConfig if provided, otherwise use sensible defaults
    if (initialConfig) {
      // console.log(`🔄 RSI: Initializing with saved config for ${symbol}:`, initialConfig);
      return {
        period: initialConfig.period || 14,
        overbought: initialConfig.overbought || 70,
        oversold: initialConfig.oversold || 30,
        isActive: initialConfig.isActive || false,
        tradeAmountPercent: initialConfig.tradeAmountPercent || 10,
        quantity: initialConfig.quantity ?? 0.5, // Default to 0.5 if not set
        profitThreshold: initialConfig.profitThreshold ?? 5, // Default to $5 if not set
        lossThreshold: initialConfig.lossThreshold ?? 3 // Default to $3 if not set
      };
    }
    
    // Sensible default configuration for new strategies
    return {
      period: 14,
      overbought: 70,
      oversold: 30,
      isActive: false,
      tradeAmountPercent: 10, // Default 10% of balance
      quantity: 0.5,         // Default quantity
      profitThreshold: 5,   // Default profit threshold
      lossThreshold: 3      // Default loss threshold
    };
  });
  
  const [currentSignal, setCurrentSignal] = useState<'buy' | 'sell' | null>(null);
  const [lastSignal, setLastSignal] = useState<'buy' | 'sell' | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false); // Track if user has made changes

  // Use external isRunning if provided, otherwise use internal state
  const actualIsRunning = externalIsRunning !== undefined ? externalIsRunning : isRunning;

  // 🔥 CRITICAL FIX: Update config when initialConfig changes, but only if user hasn't edited
  useEffect(() => {
    if (initialConfig && !hasUserEdited) {
      // console.log(`🔄 RSI: Updating config from initialConfig for ${symbol}:`, initialConfig);
      setConfig({
        period: initialConfig.period || 14,
        overbought: initialConfig.overbought || 70,
        oversold: initialConfig.oversold || 30,
        isActive: initialConfig.isActive || false,
        tradeAmountPercent: initialConfig.tradeAmountPercent || 10,
        quantity: initialConfig.quantity ?? 0.5, // Default to 0.5 if not set
        profitThreshold: initialConfig.profitThreshold ?? 5, // Default to $5 if not set
        lossThreshold: initialConfig.lossThreshold ?? 3 // Default to $3 if not set
      });
    }
  }, [initialConfig?.quantity, initialConfig?.profitThreshold, initialConfig?.lossThreshold, initialConfig?.period, initialConfig?.overbought, initialConfig?.oversold, initialConfig?.isActive, initialConfig?.tradeAmountPercent, symbol, hasUserEdited]);

  // Notify parent component when config changes
  useEffect(() => {
    onConfigUpdate?.(config);
  }, [config, onConfigUpdate]);

  // Simulate RSI calculation (in real implementation, this would use actual market data)
  const calculateRSI = useCallback(() => {
    if (!currentPrice || !actualIsRunning) {
      return;
    }
    
    const price = parseFloat(currentPrice);
    
    // Simplified RSI logic for demonstration
    // In real implementation, you would calculate RSI based on price changes
    const randomRSI = Math.random() * 100; // Simulate RSI value 0-100
    

    
    // Simulate RSI signals
    // Buy when RSI is oversold (below 30)
    // Sell when RSI is overbought (above 70)
    if (randomRSI < config.oversold) {
      setCurrentSignal('buy');
      setLastSignal('buy');
      onSignalUpdate?.('buy');
    } else if (randomRSI > config.overbought) {
      setCurrentSignal('sell');
      setLastSignal('sell');
      onSignalUpdate?.('sell');
    } else {
      setCurrentSignal(null);
      onSignalUpdate?.(null);
    }
  }, [currentPrice, actualIsRunning, config.period, config.overbought, config.oversold, onSignalUpdate]);

  // Execute AI trade based on signal
  const executeAITrade = useCallback(async (signal: 'buy' | 'sell') => {
    if (!currentPrice || !accountBalance) {
      return;
    }

    const currentBalance = accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance;
    const tradeAmountUSD = currentBalance * 0.20; // 20% of balance
    const amount = Math.max(1.10, tradeAmountUSD / parseFloat(currentPrice)); // Minimum 1.10 quantity



    // Check if we have enough balance
    if (tradeAmountUSD > currentBalance) {
      return;
    }

    // Check for existing AI positions of the same side (Buy/Sell)
    const existingPositions = openPositions.filter(position => 
      position.isAI && 
      position.symbol === symbol && 
      position.type === (signal === 'buy' ? 'Buy' : 'Sell')
    );

    // If there's already a position of the same side, don't open a new one
    if (existingPositions.length > 0) {
      return;
    }

    try {
      const positionData = {
        symbol: symbol,
        type: (signal === 'buy' ? 'Buy' : 'Sell') as 'Buy' | 'Sell',
        size: amount.toFixed(6),
        entry: currentPrice,
        current: currentPrice,
        pnl: '0.00',
        pnlPercent: '0.00%',
        leverage: 100,
        orderType: 'market' as const, // AI trades are always market orders
      };

      const newPosition = await addOpenPosition(positionData);
      
      // Deduct margin from account balance for AI trades (same as manual trades)
      if (updateBalance && accountBalance && newPosition) {
        const positionValue = amount * parseFloat(currentPrice);
        const marginRequired = positionValue / 100; // 100x leverage
        const currentBalance = accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance;
        const newBalance = currentBalance - marginRequired;
        await updateBalance(accountType, newBalance);
      }
    } catch (error) {
      console.error('Error adding position:', error);
      
      // Fallback to order confirmation modal
      if (setOrderDetails) {
        setOrderDetails({
          symbol: symbol,
          side: signal,
          type: 'market',
          amount: parseFloat(amount.toFixed(6)),
          price: parseFloat(currentPrice),
          leverage: 100,
          stopPrice: undefined
        });
        
        if (showConfirmationPopup) {
          setShowOrderConfirmation(true);
        }
      }
    }
  }, [currentPrice, accountBalance, accountType, config.tradeAmountPercent, symbol, addOpenPosition, setOrderDetails, setShowOrderConfirmation, showConfirmationPopup, updateBalance]);

  // Monitor for signal changes and execute trades
  useEffect(() => {
    if (currentSignal && currentSignal !== lastSignal) {
      executeAITrade(currentSignal);
    }
  }, [currentSignal, lastSignal, executeAITrade]);

  // Calculate RSI periodically
  useEffect(() => {
    if (!actualIsRunning) return;
    
    const interval = setInterval(calculateRSI, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [calculateRSI, actualIsRunning]);

  const handleConfigChange = (field: keyof RSIConfig, value: number | boolean) => {
    setHasUserEdited(true); // Mark that user has made changes
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-4">
      {/* Strategy Configuration */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Period:</span>
          <input
            type="number"
            value={config.period}
            onChange={(e) => handleConfigChange('period', parseInt(e.target.value))}
            className="w-[30%] px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            min="5"
            max="50"
          />
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Overbought:</span>
          <input
            type="number"
            value={config.overbought}
            onChange={(e) => handleConfigChange('overbought', parseInt(e.target.value))}
            className="w-[30%] px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            min="60"
            max="90"
          />
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Oversold:</span>
          <input
            type="number"
            value={config.oversold}
            onChange={(e) => handleConfigChange('oversold', parseInt(e.target.value))}
            className="w-[30%] px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            min="10"
            max="40"
          />
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Trade Amount:</span>
          <div className="flex items-center space-x-1 w-[30%]">
            <span className="text-xs theme-text-quaternary">%</span>
            <input
              type="number"
              value={config.tradeAmountPercent}
              onChange={(e) => handleConfigChange('tradeAmountPercent', parseFloat(e.target.value))}
              className="flex-1 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="1"
              max="100"
              step="1"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Quantity:</span>
          <div className="flex items-center space-x-1 w-[30%]">
            <input
              type="number"
              value={config.quantity}
              onChange={(e) => handleConfigChange('quantity', parseFloat(e.target.value))}
              className="flex-1 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="0.01"
              max="1000"
              step="0.01"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Profit Threshold:</span>
          <div className="flex items-center space-x-1 w-[30%]">
            <span className="text-xs theme-text-quaternary">$</span>
            <input
              type="number"
              value={config.profitThreshold}
              onChange={(e) => handleConfigChange('profitThreshold', parseFloat(e.target.value))}
              className="flex-1 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="0.01"
              max="100"
              step="0.01"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Loss Threshold:</span>
          <div className="flex items-center space-x-1 w-[30%]">
            <span className="text-xs theme-text-quaternary">$</span>
            <input
              type="number"
              value={config.lossThreshold}
              onChange={(e) => handleConfigChange('lossThreshold', parseFloat(e.target.value))}
              className="flex-1 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="0.01"
              max="100"
              step="0.01"
            />
          </div>
        </div>
        
        {/* Trade Amount Display */}
        {accountBalance && (
          <div className="flex items-center justify-between pt-2 border-t theme-border">
            <span className="text-xs theme-text-quaternary">Trade Value:</span>
            <span className="text-xs font-medium theme-text-primary">
              ${((accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance) * config.tradeAmountPercent / 100).toFixed(2)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default RSIStrategy;
